export const D610_DEGRADATION_RATE_SETTING_KEY = "d610_degradation_rate_matrix";
export const D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY =
  "d610_degradation_rate_default_band";
export const D610_SCORE_METADATA_SETTING_KEY = "d610_score_metadata_matrix";
export const D610_DEGRADATION_RATE_BANDS = ["lower", "mid", "upper"];

export const DEFAULT_D610_DEGRADATION_RATE_MATRIX = [
  { score: 10, lower: 0, mid: 0.005, upper: 0.01 },
  { score: 9, lower: 0.01, mid: 0.02, upper: 0.03 },
  { score: 8, lower: 0.03, mid: 0.065, upper: 0.1 },
  { score: 7, lower: 0.1, mid: 0.2, upper: 0.3 },
  { score: 6, lower: 0.3, mid: 0.65, upper: 1 },
  { score: 5, lower: 1, mid: 2, upper: 3 },
  { score: 4, lower: 3, mid: 6.5, upper: 10 },
  { score: 3, lower: 10, mid: 13, upper: 16 },
  { score: 2, lower: 16, mid: 24.5, upper: 33 },
  { score: 1, lower: 33, mid: 41.5, upper: 50 },
  { score: 0, lower: 50, mid: 75, upper: 100 },
];

export const DEFAULT_D610_SCORE_METADATA_MATRIX = [
  {
    score: 10,
    sslgGrade: "A",
    budgetAppName: "Excellent",
    budgetAppAbbreviation: "E",
    color: "#3B8D2D",
  },
  {
    score: 9,
    sslgGrade: "A",
    budgetAppName: "Very Good",
    budgetAppAbbreviation: "VG",
    color: "#8DD55A",
  },
  {
    score: 8,
    sslgGrade: "A",
    budgetAppName: "Good",
    budgetAppAbbreviation: "G",
    color: "#D7F4D0",
  },
  {
    score: 7,
    sslgGrade: "A",
    budgetAppName: "Fair",
    budgetAppAbbreviation: "F",
    color: "#F3C15A",
  },
  {
    score: 6,
    sslgGrade: "B",
    budgetAppName: "Fair",
    budgetAppAbbreviation: "F",
    color: "#F5B6A0",
  },
  {
    score: 5,
    sslgGrade: "B",
    budgetAppName: "Poor",
    budgetAppAbbreviation: "P",
    color: "#FF575C",
  },
  {
    score: 4,
    sslgGrade: "C",
    budgetAppName: "Poor",
    budgetAppAbbreviation: "P",
    color: "#FF1F2D",
  },
  {
    score: 3,
    sslgGrade: "C",
    budgetAppName: "Very Poor",
    budgetAppAbbreviation: "VP",
    color: "#B80000",
  },
  {
    score: 2,
    sslgGrade: "D",
    budgetAppName: "Critical",
    budgetAppAbbreviation: "Critical",
    color: "#FF00FF",
  },
  {
    score: 1,
    sslgGrade: "D",
    budgetAppName: "Critical",
    budgetAppAbbreviation: "Critical",
    color: "#F000FF",
  },
  {
    score: 0,
    sslgGrade: "D",
    budgetAppName: "Critical",
    budgetAppAbbreviation: "Critical",
    color: "#D913E6",
  },
];

const toFiniteNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeTextValue = (value, fallback = "") => {
  const normalizedValue = String(value ?? "").trim();
  return normalizedValue !== "" ? normalizedValue : fallback;
};

const normalizeHexColor = (value, fallback) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toUpperCase();

  if (/^#[0-9A-F]{6}$/.test(normalizedValue)) {
    return normalizedValue;
  }

  return String(fallback || "#2563EB")
    .trim()
    .toUpperCase();
};

const roundValue = (value, digits = 2) => {
  if (!Number.isFinite(value)) return null;
  return Number(value.toFixed(digits));
};

export const normalizeD610DegradationRateMatrix = (value) => {
  const incomingRows = Array.isArray(value) ? value : [];
  const incomingByScore = new Map(
    incomingRows.map((row) => [Number(row?.score), row]),
  );

  return DEFAULT_D610_DEGRADATION_RATE_MATRIX.map((defaultRow) => {
    const incomingRow = incomingByScore.get(defaultRow.score) || {};

    return {
      score: defaultRow.score,
      lower: toFiniteNumber(incomingRow.lower, defaultRow.lower),
      mid: toFiniteNumber(incomingRow.mid, defaultRow.mid),
      upper: toFiniteNumber(incomingRow.upper, defaultRow.upper),
    };
  });
};

export const normalizeD610ScoreMetadataMatrix = (value) => {
  const incomingRows = Array.isArray(value) ? value : [];
  const incomingByScore = new Map(
    incomingRows.map((row) => [Number(row?.score), row]),
  );

  return DEFAULT_D610_SCORE_METADATA_MATRIX.map((defaultRow) => {
    const incomingRow = incomingByScore.get(defaultRow.score) || {};

    return {
      score: defaultRow.score,
      sslgGrade: normalizeTextValue(incomingRow.sslgGrade, defaultRow.sslgGrade),
      budgetAppName: normalizeTextValue(
        incomingRow.budgetAppName,
        defaultRow.budgetAppName,
      ),
      budgetAppAbbreviation: normalizeTextValue(
        incomingRow.budgetAppAbbreviation,
        defaultRow.budgetAppAbbreviation,
      ),
      color: normalizeHexColor(incomingRow.color, defaultRow.color),
    };
  });
};

export const normalizeD610DegradationRateDefaultBand = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();

  return D610_DEGRADATION_RATE_BANDS.includes(normalizedValue)
    ? normalizedValue
    : "mid";
};

export const parseD610PercentageInput = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : Number.NaN;
  }

  const normalizedValue = String(value || "")
    .trim()
    .replace(/%$/, "")
    .trim();

  if (normalizedValue === "") {
    return Number.NaN;
  }

  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

export const interpolateLinearValue = ({
  value,
  startX,
  endX,
  startY,
  endY,
  digits = 2,
}) => {
  if (
    !Number.isFinite(value) ||
    !Number.isFinite(startX) ||
    !Number.isFinite(endX) ||
    !Number.isFinite(startY) ||
    !Number.isFinite(endY)
  ) {
    return null;
  }

  if (startX === endX) {
    throw new Error("Cannot interpolate with duplicate x-axis values.");
  }

  const interpolatedValue =
    startY + ((endY - startY) * (value - startX)) / (endX - startX);

  return roundValue(interpolatedValue, digits);
};

const resolveStrictDefaultBand = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();

  if (!D610_DEGRADATION_RATE_BANDS.includes(normalizedValue)) {
    throw new Error("D610 default band setting is missing or invalid.");
  }

  return normalizedValue;
};

const resolveStrictInterpolationRows = (matrix, defaultBand) => {
  if (!Array.isArray(matrix)) {
    throw new Error("D610 degradation rate matrix is missing or invalid.");
  }

  const rows = matrix
    .map((row) => {
      const score = Number(row?.score);
      const rate = Number(row?.[defaultBand]);

      if (!Number.isFinite(score) || !Number.isFinite(rate)) {
        throw new Error(
          `D610 degradation rate matrix contains a non-numeric ${defaultBand} value.`,
        );
      }

      return { score, rate };
    })
    .sort((left, right) => left.rate - right.rate);

  if (rows.length < 2) {
    throw new Error("D610 degradation rate matrix requires at least two rows.");
  }

  for (let index = 1; index < rows.length; index += 1) {
    if (rows[index].rate <= rows[index - 1].rate) {
      throw new Error(
        `D610 ${defaultBand} values must be strictly increasing for interpolation.`,
      );
    }
  }

  return rows;
};

const resolveStrictScoreMetadataRows = (metadataMatrix) => {
  if (!Array.isArray(metadataMatrix)) {
    throw new Error("D610 score metadata matrix is missing or invalid.");
  }

  const rows = metadataMatrix.map((row) => {
    const score = Number(row?.score);
    if (!Number.isFinite(score)) {
      throw new Error("D610 score metadata matrix contains a non-numeric score.");
    }

    return {
      ...row,
      score,
      color: normalizeHexColor(row?.color, "#2563EB"),
    };
  });

  if (rows.length === 0) {
    throw new Error("D610 score metadata matrix requires at least one row.");
  }

  return rows;
};

export const interpolateD610ScoreFromRate = (
  percentage,
  matrix,
  defaultBand,
  digits = 2,
) => {
  const normalizedPercentage = parseD610PercentageInput(percentage);
  if (!Number.isFinite(normalizedPercentage)) {
    return null;
  }

  const resolvedDefaultBand = resolveStrictDefaultBand(defaultBand);
  const rows = resolveStrictInterpolationRows(matrix, resolvedDefaultBand);

  if (normalizedPercentage <= rows[0].rate) {
    return roundValue(rows[0].score, digits);
  }

  if (normalizedPercentage >= rows[rows.length - 1].rate) {
    return roundValue(rows[rows.length - 1].score, digits);
  }

  for (let index = 1; index < rows.length; index += 1) {
    const previousRow = rows[index - 1];
    const currentRow = rows[index];

    if (normalizedPercentage === currentRow.rate) {
      return roundValue(currentRow.score, digits);
    }

    if (normalizedPercentage < currentRow.rate) {
      return interpolateLinearValue({
        value: normalizedPercentage,
        startX: previousRow.rate,
        endX: currentRow.rate,
        startY: previousRow.score,
        endY: currentRow.score,
        digits,
      });
    }
  }

  return roundValue(rows[rows.length - 1].score, digits);
};

export const getD610InterpolationContext = (categorySettings = {}) => {
  const matrixSetting = categorySettings[D610_DEGRADATION_RATE_SETTING_KEY];
  const defaultBandSetting =
    categorySettings[D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY];

  if (!matrixSetting) {
    throw new Error("Missing D610 degradation rate matrix setting.");
  }

  if (!defaultBandSetting) {
    throw new Error("Missing D610 default band setting.");
  }

  return {
    matrix: Array.isArray(matrixSetting)
      ? matrixSetting
      : matrixSetting?.value,
    defaultBand: resolveStrictDefaultBand(
      typeof defaultBandSetting === "string"
        ? defaultBandSetting
        : defaultBandSetting?.value,
    ),
  };
};

export const interpolateD610ScoreFromSettings = (
  percentage,
  categorySettings,
  digits = 2,
) => {
  const { matrix, defaultBand } = getD610InterpolationContext(categorySettings);

  return interpolateD610ScoreFromRate(
    percentage,
    matrix,
    defaultBand,
    digits,
  );
};

export const createD610ScoreInterpolator = (
  categorySettings,
  digits = 2,
) => {
  const { matrix, defaultBand } = getD610InterpolationContext(categorySettings);

  return (percentage) =>
    interpolateD610ScoreFromRate(percentage, matrix, defaultBand, digits);
};

export const getNearestD610MetadataForScore = (score, metadataMatrix) => {
  const normalizedScore = Number(score);
  if (!Number.isFinite(normalizedScore)) {
    return null;
  }

  const rows = resolveStrictScoreMetadataRows(metadataMatrix);
  return rows.reduce((closestRow, currentRow) => {
    if (!closestRow) {
      return currentRow;
    }

    const closestDistance = Math.abs(normalizedScore - closestRow.score);
    const currentDistance = Math.abs(normalizedScore - currentRow.score);

    if (currentDistance < closestDistance) {
      return currentRow;
    }

    if (currentDistance === closestDistance && currentRow.score > closestRow.score) {
      return currentRow;
    }

    return closestRow;
  }, null);
};

export const getD610ColorForScore = (score, metadataMatrix) => {
  return getNearestD610MetadataForScore(score, metadataMatrix)?.color || null;
};